import { HeroBackground, ProductMain } from '@prishapolicy/shared/ui';
import { whoShouldBuyIcons } from '@prishapolicy/w3/assets';
import {
  ComparisonTable,
  ComparisonTableProps,
  Faq,
  firefaqList,
  FireImportantCharacteristics,
  getPurchaseFlowHeroForms,
  HeroTicker,
  ProductCategoryPageProps,
  productsPathAdder,
  ProtectToday,
  useW3PurchaseFlow,
  WhoShouldBuy,
  WhoShouldBuyProps,
  WhyChooseUs,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import fireInsurance from './fire-insurance-house.png';
import fireTyphoon from './fire-typhoon.png';

const productCategorySlug = 'fire-insurance';

export function FireInsurance(props: ProductCategoryPageProps) {
  const whoShouldBuyCards: WhoShouldBuyProps['cards'] = [
    {
      image: whoShouldBuyIcons.educationalInstitutesIcon,
      title: 'Educational Institutes',
      content: 'Schools, Universities, Online Coaching Classes',
    },
    {
      image: whoShouldBuyIcons.businessesIcon,
      title: 'Businesses',
      content: 'Retailers, shopkeepers, godowns',
    },
    {
      image: whoShouldBuyIcons.manufacturingFirmsIcon,
      title: 'Manufacturing Firms',
      content: 'Industrial sites, factories, cottage industries',
    },
    {
      image: whoShouldBuyIcons.individualIcon,
      title: 'Individual',
      content: 'House, garage, personal properties that are at risk',
    },
    {
      image: whoShouldBuyIcons.serviceProvidersIcon,
      title: 'Service Providers',
      content: 'Medical clinics, restaurants, lodges',
    },
    {
      image: whoShouldBuyIcons.otherInstitutesIcon,
      title: 'Other Institutes',
      content: 'Banks, research, companies etc',
    },
  ];

  const comparisonTableData: ComparisonTableProps['data'] = {
    heading: 'Standard Fire and Special Perils Insurance',
    columns: ['Inclusions', 'Exclusions'],
    categoryWise: [
      {
        name: 'Type of Calamity',
        rows: [
          {
            name: 'Fire',
            values: [
              'Destruction and damage to personal property insured',
              'Damages caused by natural heating or drying processes',
            ],
          },
          {
            name: 'Lightning',
            values: ['Cracks in the roof, building, short circuits and other related fires.', 'None'],
          },
          {
            name: 'Explosion',
            values: [
              'Damages to the property by exploding items',
              'Damages caused by non-domestic boilers, and other vessels that generate steam',
            ],
          },
          {
            name: 'Aircraft Damage',
            values: ['Loss or destruction caused by Aircraft and articles dropped', 'Damages caused by Pressure waves'],
          },
          {
            name: 'Aircraft Damage',
            values: ['Loss or destruction caused by Aircraft and articles dropped', 'Damages caused by Pressure waves'],
          },
          {
            name: 'Riot, Strike and Malicious Damage',
            values: ['Physical damage and fire caused by external forces', 'Fires caused by burglary, theft, larceny'],
          },
          {
            name: 'Impact Damage',
            values: ['Fires caused by impact from a vehicle or animal', 'Damage to the insured person or employees'],
          },
          {
            name: 'Subsidence & Landslides',
            values: [
              'A fire caused by subsidence (sinking of the ground) of site',
              'Damage caused by normal cracking, settlement or bedding down of new structures, coastal or river erosion',
            ],
          },
          {
            name: 'Bursting & Overflowing of Water Tanks, Apparatus, and Pipes',
            values: ['Damages caused by these actions to property insured', 'None'],
          },
          {
            name: 'Leakage from Automatic Sprinkler Installations',
            values: ['Physical damage and fire caused by external forces', 'Fires caused by burglary, theft, larceny'],
          },
          {
            name: 'Missile Testing Operations',
            values: ['All fires caused', 'None'],
          },
        ],
      },
    ],
  };
  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);
  return (
    <>
      <NextSeo
        title="Fire Insurance | PrishaPolicy"
        description="Fire Insurance : Secure yourself from all kinds of fire and burglary hazards with PrishaPolicy's expert team."
      />
      <ProductMain color="red">
        <HeroBackground backgroundColor="red-400" PurchaseFlowFormRenderer={FormRenderer}>
          <div className="grid-cols-2 lg:grid ">
            <div className="flex flex-col justify-between">
              <div>
                <h1 className="heading-1 pb-3">Whatever may happen, we&apos;ve got your back.</h1>
                <p className="text-title-6 max-w-[600px]">
                  Secure yourself from all kinds of fire and burglary hazards with PrishaPolicy&apos;s expert team.
                </p>
              </div>
              <IconRenderer />
            </div>
            <div className="hidden items-end justify-center lg:flex">
              <Image src={fireInsurance} alt="contact us" className="w-3/4" />
            </div>
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday maxPerRow={5} items={props.productPaths} productCategoryPrefix={productCategorySlug} />
        <div className="container grid-cols-2 gap-x-24 py-24 lg:grid">
          <div className="flex flex-col justify-center">
            <h3 className="lg:heading-3 heading-4">Fire Insurance — what is it?</h3>
            <p className="prose mt-4">
              As the name suggests, fire insurance pays the bills and expenses that are incurred by the policyholder in
              case of a fire.
              <br />
              <br />
              The fire may be caused by a host of different reasons: natural disasters such as storms and lightning or
              man-made disasters like faulty wiring or riots, a fire insurance policy will cover the cost of damage and
              repairs.
              <br />
              <br />
              In legal terms, this is known as Standard Fire and Special Perils Insurance.
            </p>
          </div>
          <div className="grid place-items-center ">
            <Image src={fireTyphoon} alt="fire insurance" />
          </div>
          <div className="col-span-2 mt-16">
            <h3 className="heading-3">The Necessity of Fire Insurance </h3>
            <p className="mt-3 max-w-[1100px]">
              Fire insurance covers a policyholder against fire loss or damage from a number of sources. These include
              fires brought about by electricity, such as faulty wiring and gas explosions, as well as those caused by
              lightning and natural disasters. A burst and overflowing water tank or pipes may also be covered by the
              policy.
            </p>
          </div>
          <div className="col-span-2">
            <WhoShouldBuy cards={whoShouldBuyCards} name="Fire Insurance" />
          </div>
        </div>
        <FireImportantCharacteristics />
        <ComparisonTable comparisonTableData={comparisonTableData} />
        <WhyChooseUs />
        <Faq list={firefaqList} />
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  // protectCardCheck(productCategorySlug, productsPathAdder(productCategorySlug));
  const productPaths = productsPathAdder(productCategorySlug);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}
export default FireInsurance;
